import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * AuthCallback — handles the Google OAuth session_id exchange.
 * Rendered synchronously in App.js when session_id is detected in the URL hash.
 * Uses useRef to prevent double-processing in StrictMode.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

    // Clear hash immediately
    window.history.replaceState(null, '', window.location.pathname);

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    // Safety timeout — if something hangs, redirect to login after 15s
    const safetyTimer = setTimeout(() => {
      setStatus('timeout');
      navigate('/login', {
        replace: true,
        state: { googleError: 'Sign-in took too long. Please try again.' }
      });
    }, 15000);

    const doLogin = async () => {
      try {
        const result = await googleLogin(sessionId);
        clearTimeout(safetyTimer);
        if (result?.is_new_user) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        clearTimeout(safetyTimer);
        // Extract the most useful error message
        let errorMsg = 'Google sign-in failed. Please try again.';
        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;
          errorMsg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
          errorMsg = 'Network error — unable to reach the server. Please check your connection and try again.';
        } else if (err.message) {
          errorMsg = err.message;
        }
        navigate('/login', {
          replace: true,
          state: { googleError: errorMsg }
        });
      }
    };

    doLogin();

    return () => clearTimeout(safetyTimer);
  }, [googleLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback-loading">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#D94E18] mx-auto" />
        <p className="text-zinc-500 text-sm">
          {status === 'timeout' ? 'Taking longer than expected...' : 'Signing you in...'}
        </p>
      </div>
    </div>
  );
}
